import { getPlatform } from 'src/utils/platform';
import { App } from '@capacitor/app';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { applyUpdateUrl } from 'src/modules/auto-updater';
import { readDenylist } from './bundleDenylist';
import type {
  DecideUpdateInput,
  InstalledBundle,
  LatestVersionInfo,
} from './updateCheck';

export const PLUGIN_VERSION_READ_TIMEOUT_MS = 1500;

const readPluginVersion = async (): Promise<string | undefined> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<undefined>((resolve) => {
    timer = setTimeout(() => resolve(undefined), PLUGIN_VERSION_READ_TIMEOUT_MS);
  });
  try {
    return await Promise.race([
      CapacitorUpdater.getPluginVersion().then((result) => result.version),
      timeout,
    ]);
  } catch {
    return undefined;
  } finally {
    clearTimeout(timer);
  }
};

const readBuiltinVersion = async (): Promise<string> => {
  try {
    return (await CapacitorUpdater.getBuiltinVersion()).version;
  } catch {
    return '0.0.0';
  }
};

const readNativeBuildNumber = async (): Promise<number> => {
  try {
    const build = Number((await App.getInfo()).build);
    return Number.isFinite(build) ? build : Number.POSITIVE_INFINITY;
  } catch {
    return Number.POSITIVE_INFINITY;
  }
};

export const gatherUpdateInput = async (): Promise<DecideUpdateInput> => {
  // Must precede getLatest(): the stored environment override decides which backend publishes
  // "latest", so checking first would query the wrong environment.
  await applyUpdateUrl();

  const currentBundle = await CapacitorUpdater.current();

  let latest: LatestVersionInfo | null = null;
  try {
    latest = (await CapacitorUpdater.getLatest()) as unknown as LatestVersionInfo;
  } catch {
    latest = null;
  }

  let installed: InstalledBundle[] = [];
  try {
    const result = await CapacitorUpdater.list();
    installed = (result?.bundles ?? []) as unknown as InstalledBundle[];
  } catch {
    // A failed listing only costs us the reuse optimisation, never correctness.
    installed = [];
  }

  return {
    currentVersion: currentBundle.bundle.version,
    builtinVersion: await readBuiltinVersion(),
    platform: getPlatform(),
    latest,
    installed,
    nativeBuildNumber: await readNativeBuildNumber(),
    denied: readDenylist(Date.now()),
    pluginVersion: await readPluginVersion(),
  };
};

export const getCurrentBundleId = async (): Promise<string> =>
  (await CapacitorUpdater.current()).bundle.id;

export const getCurrentBundleVersion = async (): Promise<string> =>
  (await CapacitorUpdater.current()).bundle.version;
